import { useMemo } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Chip from '@/components/ui/Chip.tsx';
import { EmptyValue } from '@/components/ui/EmptyValue.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import { cn } from '@/lib/utils.ts';
import { fullNameOf } from '@/utils/personName.ts';
import { TourOptionOccupancyDto } from '@/features/dashboard/tours/types/dashboard/dto/TourOptionOccupancyDto.ts';
import { TourOptionParticipantDto } from '@/features/dashboard/tours/types/dashboard/dto/TourOptionParticipantDto.ts';
import {
  isOptionFilterSelected,
  OptionFilter,
} from '@/features/dashboard/tours/components/options/optionFilters.ts';

interface OptionCrossTableProps {
  options: TourOptionOccupancyDto[];
  participants: TourOptionParticipantDto[];
  filters: OptionFilter[];
}

/** Pierwsza kolumna stoi w miejscu przy przewijaniu — bez nazwiska wiersz nic nie znaczy. */
const STICKY_CELL = 'sticky left-0 z-10';

/**
 * Kto co wybrał, opcja po opcji. `<Table>` odpada: tamten komponent stoi na
 * `table-definition` i stronicowanej odpowiedzi z sieci, a tutaj wiersze są
 * lokalne, a kolumny wynikają z liczby opcji wyjazdu.
 */
const OptionCrossTable = ({
  options,
  participants,
  filters,
}: OptionCrossTableProps) => {
  const columns = useMemo<ColumnDef<TourOptionParticipantDto>[]>(
    () => [
      {
        id: 'participant',
        header: 'Uczestnik',
        cell: ({ row }) => (
          <span className="block min-w-0">
            <span className="block truncate text-[15px] font-semibold text-ink">
              {fullNameOf(
                row.original.participant.firstName,
                row.original.participant.lastName,
              )}
            </span>
            <span className="block truncate text-[13px] text-neutral-500">
              {row.original.bookingCode}
            </span>
          </span>
        ),
      },
      ...options.map<ColumnDef<TourOptionParticipantDto>>((option) => ({
        id: option.optionId,
        header: option.name,
        cell: ({ row }) => {
          const selectedVariantIds =
            row.original.selectedOptions[option.optionId] ?? [];
          if (selectedVariantIds.length === 0) return <EmptyValue />;

          return (
            <span className="flex flex-wrap gap-1">
              {selectedVariantIds.map((variantId) => {
                const variant = option.variants.find(
                  (candidate) => candidate.variantId === variantId,
                );
                const matched = isOptionFilterSelected(
                  filters,
                  option.optionId,
                  variantId,
                );

                return (
                  <Chip
                    key={variantId}
                    size="sm"
                    label={variant?.name ?? variantId}
                    tone={matched ? 'accent' : 'neutral'}
                    fill={matched ? 'soft' : 'outline'}
                  />
                );
              })}
            </span>
          );
        },
      })),
    ],
    [options, filters],
  );

  const table = useReactTable({
    data: participants,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const filteredOptionIds = new Set(filters.map((filter) => filter.optionId));

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b border-neutral-100">
              {headerGroup.headers.map((header, index) => (
                <th
                  key={header.id}
                  className={cn(
                    columnLabelClass,
                    'whitespace-nowrap bg-basic-white px-4 py-2.5 align-bottom',
                    index === 0 && cn(STICKY_CELL, 'min-w-[200px]'),
                    filteredOptionIds.has(header.column.id) &&
                      'text-accent-800',
                  )}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-neutral-100 last:border-b-0"
            >
              {row.getVisibleCells().map((cell, index) => (
                <td
                  key={cell.id}
                  className={cn(
                    'bg-basic-white px-4 py-2.5 align-middle text-[13px]',
                    index === 0 && cn(STICKY_CELL, 'max-w-[220px]'),
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default OptionCrossTable;
